import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetOrganisationChargeQuery,
  useGetOrganisationChargesQuery,
  useGetPlatformAdminOrganisationQuery,
} from "@/store/admin_api";
import Organisation from "@/model/interface/Organisation";
import OrganisationCombobox from "./OrganisationCombobox";
import ChargeDetailsSheet from "@/components/pages/billing/ChargeDetailsSheet";
import { CHARGE_TYPE_LABELS, ChargeType } from "@/enum/ChargeType";
import { CHARGE_PAGE_SIZE_OPTIONS, chargeUserName } from "@/util/charge";
import { formatDateTime } from "@/util/date";
import React from "react";

export default function OrganisationCharges() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOrg, setSelectedOrg] = useState<Organisation | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(CHARGE_PAGE_SIZE_OPTIONS[0]);
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);

  const linkedOrgId = searchParams.get("id");
  const { data: linkedOrg } = useGetPlatformAdminOrganisationQuery(linkedOrgId ?? "", {
    skip: !linkedOrgId || selectedOrg?.id === linkedOrgId,
  });

  const { data, isLoading } = useGetOrganisationChargesQuery(
    {
      organisationId: selectedOrg?.id ?? "",
      page,
      size: pageSize,
      type: typeFilter !== "ALL" ? typeFilter : undefined,
    },
    { skip: !selectedOrg },
  );

  const { data: selectedCharge, isFetching: isFetchingCharge } = useGetOrganisationChargeQuery(
    { organisationId: selectedOrg?.id ?? "", chargeId: selectedChargeId ?? "" },
    { skip: !selectedOrg || !selectedChargeId },
  );

  const charges = data?.items;
  const totalAmount = charges?.reduce((sum, c) => sum + (c.amount ?? 0), 0) ?? 0;
  const totalFees = charges?.reduce((sum, c) => sum + (c.platformFee ?? 0), 0) ?? 0;

  const handleOrgChange = (org: Organisation | null) => {
    setSelectedOrg(org);
    setPage(0);
    if (org) {
      setSearchParams({ id: org.id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    if (linkedOrg && linkedOrg.id === linkedOrgId && selectedOrg?.id !== linkedOrg.id) {
      setSelectedOrg(linkedOrg);
      setPage(0);
    }
  }, [linkedOrg, linkedOrgId, selectedOrg]);

  const handleTypeChange = (value: string) => {
    setTypeFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-1">Charges</h2>
          <p className="text-muted-foreground text-sm">View charges recorded for an organisation.</p>
        </div>
        <OrganisationCombobox value={selectedOrg} onChange={handleOrgChange} />
      </div>

      {!selectedOrg && (
        <div className="border rounded-lg p-12 text-center text-muted-foreground">
          Select an organisation to view its charges.
        </div>
      )}

      {selectedOrg && isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {selectedOrg && !isLoading && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Total (this page)</p>
              <p className="text-2xl font-bold">${totalAmount.toFixed(2)}</p>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Platform Fees</p>
              <p className="text-2xl font-bold">${totalFees.toFixed(2)}</p>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Carrier Cost</p>
              <p className="text-2xl font-bold">${(totalAmount - totalFees).toFixed(2)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Select value={typeFilter} onValueChange={handleTypeChange}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All types</SelectItem>
                {Object.values(ChargeType).map((chargeType) => (
                  <SelectItem key={chargeType} value={chargeType}>
                    {CHARGE_TYPE_LABELS[chargeType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data && (
              <span className="text-sm text-muted-foreground">
                {data.totalElements} charge{data.totalElements !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Carrier Cost</TableHead>
                  <TableHead className="text-right">Platform Fee</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {charges && charges.length > 0 ? (
                  charges.map((charge) => (
                    <TableRow
                      key={charge.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedChargeId(charge.id)}
                    >
                      <TableCell>
                        <Badge variant="secondary">{CHARGE_TYPE_LABELS[charge.type] ?? charge.type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{charge.description ?? "—"}</TableCell>
                      <TableCell className="text-sm">{chargeUserName(charge.user)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        ${((charge.amount ?? 0) - (charge.platformFee ?? 0)).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {(charge.platformFee ?? 0) > 0 ? `$${(charge.platformFee ?? 0).toFixed(4)}` : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium">
                        ${(charge.amount ?? 0).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(charge.createdAt)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No charges recorded.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {data && data.totalElements > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Results per page</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[80px] tabular-nums">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHARGE_PAGE_SIZE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {data.totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    Page {data.page + 1} of {data.totalPages}
                  </span>
                  <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ChargeDetailsSheet
        open={!!selectedChargeId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedChargeId(null);
          }
        }}
        charge={selectedCharge}
        isLoading={isFetchingCharge}
        showInternal
      />
    </div>
  );
}
